<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('studentScoringEntry.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('studentScoringEntry.tableEntry'),
            href: '/student-scoring-entries'
          },
          {
            text: $t('edit'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="row">
            <div class="col-md-12">
              <div class="ts-border ts-p-4 ts-rounded ts-mb-3 ts-w-full">
                <div class="col-md-12 ts-space-y-2">
                  <div class="row">
                    <div class="col-md-12">
                      <a-select
                        v-model="model.class_id"
                        style="width: 100%"
                        :options="classes"
                        :showSearch="true"
                        @change="onClassChange"
                        disabled
                      ></a-select>
                      <div
                        class="ts-text-red-600"
                        v-if="errors.has('class_id')"
                      >
                        {{ errors.first('class_id') }}
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4">
                      <label class="required ts-mb-0">{{
                        $t('studentScoringEntry.scoringCycle')
                      }}</label>
                      <a-select
                        v-model="model.scoring_cycle_id"
                        style="width: 100%"
                        :options="scoringCycle"
                        disabled
                      ></a-select>
                      <div
                        class="ts-text-red-600"
                        v-if="errors.has('scoring_cycle_id')"
                      >
                        {{ errors.first('scoring_cycle_id') }}
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div v-if="model.scoring_cycle_id != 4">
                        <label class="required ts-mb-0">{{
                          $t('studentScoringEntry.scoringCycleValue')
                        }}</label>

                        <select
                          style="width: 100%"
                          v-model.number="model.cycle_value"
                          v-if="includeValue([1, 4, 5, 7])"
                          class="form-control"
                          @change="onCycleValue"
                        >
                          <option
                            :value="null"
                            disabled
                            selected
                            style="display: none"
                          >
                            {{ $t('select') }}
                          </option>
                          <option
                            v-for="(s, index) in scoringCycleValuesList"
                            :key="index"
                            :value="s.scoring_cycle_value"
                          >
                            {{ s.scoring_cycle_description }}
                          </option>
                        </select>
                        <input
                          v-model.number="model.cycle_value"
                          type="number"
                          style="width: 100%"
                          class="form-control"
                          readonly
                          v-if="includeValue([0, 2, 3])"
                        />
                        <input
                          type="number"
                          class="form-control"
                          style="width: 100%"
                          readonly
                          v-if="model.scoring_cycle_id == null"
                        />
                        <div
                          class="ts-text-red-600"
                          v-if="errors.has('cycle_value')"
                        >
                          {{ errors.first('cycle_value') }}
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label class="ts-mb-0">{{
                        $t('studentScoringEntry.subject')
                      }}</label>
                      <a-select
                        v-model="selectedSubjectId.id"
                        style="width: 100%"
                        :options="subjects"
                        mode="multiple"
                        :maxTagCount="2"
                      ></a-select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="ts-overflow-auto">
            <NumberingScoreTable
              v-model="model"
              v-if="model.score_type == 1"
              :students="students"
              :scoreEntities="scoreEntities"
              :selectedSubjectId="selectedSubjectId"
              @loading="v => (loading = v)"
              ref="numering"
            />
            <div
              v-if="model.score_type == null"
              class="ts-h-64 ts-flex ts-items-center ts-justify-center"
            >
              <div class="ts-text-center ts-opacity-50">
                <i class="far fa-list-alt fa-4x"></i>
                <h1 class="ts-font-bold ts-text-gray-600">No Record</h1>
              </div>
            </div>
          </div>
          <div class="ts-space-x-3 ts-flex ts-justify-end ts-mt-3">
            <ts-button
              @click="$router.push({ name: 'student-scoring-entry' })"
              >{{ $t('cancel') }}</ts-button
            >
            <ts-button color="primary" @click.prevent="onStore">{{
              $t('save')
            }}</ts-button>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { debounce } from 'lodash'
import {
  formViewModel,
  getScoringCycle,
  getScoringCycleValue,
  store
} from '@/api/student-scoring-table-entry'
import NumberingScoreTable from './table-entry-component/numberingScoreTable.vue'
export default {
  name: 'scoringEntryAsTableEdit',
  components: {
    NumberingScoreTable
  },
  data() {
    return {
      errors: new Errors(),
      loading: false,
      acStatusId: 1,
      classes: [],
      scoringCycle: [],
      scoringCycleValues: [],
      students: [],
      scoreEntities: [],
      term_order: null,
      studentId: null, // only in use with edit student
      selectedSubjectId: {
        id: []
      },
      subjects: [],
      model: {
        class_id: null,
        grade_id: null,
        scoring_cycle_id: null, // 1: monthly , 2 semester
        score_type: null,
        acps_id: null,
        cycle_value: null,
        acp_id: null,
        ac_year_id: null,
        ac_term_id: null,
        program_structure_id: null,
        ac_category_id: 0,
        score_sc_bas_id: 1, // item base default
        sc_item_subject_comp_list: []
      }
    }
  },
  computed: {
    includeValue: vm => arrayValue => {
      if (arrayValue == undefined) {
        return false
      }
      return arrayValue.includes(vm.model.scoring_cycle_id)
    },
    scoringCycleValuesList() {
      if (this.model.scoring_cycle_id) {
        return this.scoringCycleValues.filter(
          s => s.scoring_cycle_id == this.model.scoring_cycle_id
        )
      }
      return this.scoringCycleValues
    }
  },
  async mounted() {
    await this.fetchScoringCycleValue()
    await this.fetchClassFunction(' ')
    await this.invokeClassChangeAction()
  },
  methods: {
    getSetParamFromQuery() {
      const {
        classId,
        studentId,
        scoringCycleId,
        cycleValue,
        programStructureId
      } = this.$route.query
      this.model.class_id = classId ? parseInt(classId) : null
      this.model.scoring_cycle_id =
        scoringCycleId !== undefined ? parseInt(scoringCycleId) : null
      this.model.cycle_value = cycleValue ? parseInt(cycleValue) : null
      this.model.program_structure_id = programStructureId
        ? parseInt(programStructureId)
        : null
      this.studentId = studentId
    },
    fetchScoringCycleValue() {
      this.loading = true
      return getScoringCycleValue()
        .then(response => {
          this.scoringCycleValues = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchScoringCyle({ acps_id }) {
      this.loading = true
      return getScoringCycle({ params: { acps_id } })
        .then(async response => {
          this.scoringCycle = response.data.map(el => ({
            value: el.scoring_cycle_id,
            label: el.scoring_cycle_name
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchClassFunction(value) {
      if (!value) return
      this.loading = true
      return formViewModel({
        params: {
          fnName: 'academicClass',
          classId: this.$route.query.classId,
          searchClass: value
          // acStatusId: this.acStatusId
        }
      })
        .then(({ data }) => {
          this.classes = data.academicClass.map(el => ({
            value: el.class_id,
            sch_id: el.sch_id,
            label: `${el.ac_year} - ${el.term_name} -${el.class_name} - ${el.class_desc} - ${el.teacher_name}`,
            acps_id: el.acps_id,
            acp_id: el.acp_id,
            ac_year_id: el.ac_year_id,
            ac_term_id: el.ac_term_id,
            term_order: el.term_order,
            grade_id: el.grade_id,
            score_type: el.score_type, // 1 numbering, 2 grading, 3 rating
            monthly_score_sc_bas_id: el.monthly_score_sc_bas_id, // 1 item base, 2 subject base
            semester_score_sc_bas_id: el.semester_score_sc_bas_id, // 1 item base, 2 subject base
            ac_category_id: el.ac_category_id // 1 សិក្សាសង្គម, 0 Normal, 2 Sciense
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchStudent() {
      this.loading = true
      return formViewModel({
        params: {
          fnName: 'students',
          class_id: this.model.class_id,
          studentId: this.studentId
        }
      })
        .then(({ data }) => {
          this.students = data.students
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchEntities() {
      this.loading = true
      return formViewModel({
        params: {
          fnName: 'scoreEntities',
          class_id: this.model.class_id,
          score_type: this.model.score_type
        }
      })
        .then(({ data }) => {
          if (data.scoreEntities !== null) {
            this.subjects = data.scoreEntities.entities.map(el => ({
              value: el.id,
              label: el.name_native + '-' + el.name
            }))
            this.selectedSubjectId.id.push(...this.subjects.map(el => el.value))
            this.scoreEntities = data.scoreEntities.entities
          }
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onClassChange(value, option) {
      this.scoreEntities = []
      // this.model.sc_item_subject_comp_list = []
      const {
        acp_id,
        acps_id,
        ac_year_id,
        ac_term_id,
        term_order,
        score_type,
        ac_category_id,
        grade_id
      } = option.data.props
      this.model.acp_id = acp_id
      this.model.acps_id = acps_id
      this.model.ac_year_id = ac_year_id
      this.model.ac_term_id = ac_term_id
      this.model.scoring_cycle_id = null
      this.model.cycle_value = null
      this.model.grade_id = grade_id
      this.model.score_type = score_type ? parseInt(score_type) : null
      this.model.ac_category_id = ac_category_id
      this.term_order = term_order
      return this.fetchScoringCyle({ acps_id: acps_id })
    },
    async scoringCycleChange() {
      const findClass = this.classes.find(
        c => c.value === parseInt(this.model.class_id)
      )
      switch (this.model.scoring_cycle_id) {
        case 1: // monthly
          this.model.score_sc_bas_id = parseInt(
            findClass.monthly_score_sc_bas_id
          )
          break
        case 2: // semester
          this.model.score_sc_bas_id = parseInt(
            findClass.semester_score_sc_bas_id
          )
          break
      }
      this.fetchEntities().then(() => {
        if ([0, 2, 3].includes(this.model.scoring_cycle_id)) {
          this.model.cycle_value = this.term_order
        } else if (this.model.scoring_cycle_id == 4) {
          this.model.cycle_value = 1
        }

        if (this.model.score_type == 1) {
          // score type numberic
          this.$refs.numering.createRecordStudentRowEntryForNumberic()
        }
      })

      return await new Promise(r => r())
    },
    clearInput() {
      this.classes = []
      this.scoringCycle = []
      this.students = []
      this.scoreEntities = []
      this.term_order = null
      this.model.class_id = null
      this.model.grade_id = null
      this.model.scoring_cycle_id = null
      this.model.score_type = null
      this.model.acps_id = null
      this.model.cycle_value = null
      this.model.acp_id = null
      this.model.ac_year_id = null
      this.model.ac_term_id = null
      this.model.program_structure_id = null
      this.model.sc_item_subject_comp_list = []
    },
    onCycleValue() {
      this.$refs.numering.onCycleValue()
    },
    onStore() {
      this.loading = true
      store(this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.onCycleValue()
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    async invokeClassChangeAction() {
      await this.onClassChange(this.model.class_id, {
        data: {
          props: this.classes.find(
            c => c.value === parseInt(this.$route.query.classId)
          )
        }
      })
      this.getSetParamFromQuery()
      await this.fetchStudent()
      return this.scoringCycleChange().then(() => {
        this.$refs.numering.onCycleValue()
      })
    }
  }
}
</script>
